import { urlDateRange, urlEnum } from '@/lib/url-state/codecs';
import { urlHref, type UrlValues } from '@/lib/url-state/url-schema';

export type MarketingGranularity = 'day' | 'week' | 'month';

export const MARKETING_GRANULARITIES: readonly MarketingGranularity[] = ['day', 'week', 'month'];

/** The period a marketing page opens on before anyone has chosen one. */
export const DEFAULT_MARKETING_PERIOD = 'last13months';

/**
 * The marketing pages' shared URL: the date range (`period`, or `from` and `to` for a custom
 * range) and the chart granularity. Every marketing page reads it with `useMarketingFilters`, and
 * they share one remembered view (`MARKETING_VIEW_ID`), so the range chosen on one page is the one
 * the sidebar opens on the next.
 */
export const MARKETING_URL = {
  range: urlDateRange({ defaultPeriod: DEFAULT_MARKETING_PERIOD }),
  granularity: urlEnum('granularity', MARKETING_GRANULARITIES, 'month'),
};

/** The last view every marketing page shares (decision 24 a), per team. */
export const MARKETING_VIEW_ID = 'marketing';

export type MarketingView = UrlValues<typeof MARKETING_URL>;

/** A link to a marketing page with this range and granularity (the rest default). */
export const marketingHref = (pathname: string, view: Partial<MarketingView>) =>
  urlHref(pathname, MARKETING_URL, view);

export type MarketingFiltersUrlState = {
  period: string;
  startDate: string;
  endDate: string;
  granularity: MarketingGranularity;
};

/** The flat shape the marketing pages read: period, start and end dates, granularity. */
export function marketingFiltersFromView(view: MarketingView): MarketingFiltersUrlState {
  return {
    period: view.range.period,
    startDate: view.range.from,
    endDate: view.range.to,
    granularity: view.granularity,
  };
}

function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

export function marketingStartDate(filters: Pick<MarketingFiltersUrlState, 'startDate'>): Date {
  return parseYmd(filters.startDate);
}

export function marketingEndDate(filters: Pick<MarketingFiltersUrlState, 'endDate'>): Date {
  return parseYmd(filters.endDate);
}

export function granularityChartLabel(g: MarketingGranularity): string {
  switch (g) {
    case 'day':
      return 'Daily';
    case 'month':
      return 'Monthly';
    default:
      return 'Weekly';
  }
}
